import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AlertCircle, CheckCircle, Lock } from 'lucide-react';
import { setPassword, getPasswordPolicy } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import TextInput from '../../components/ui/TextInput';
import PasswordPolicyChecklist from '../../components/PasswordPolicyChecklist';
import { DEFAULT_PASSWORD_POLICY, isPasswordAllowed } from '../../utils/passwordPolicy';
import flexbeeLogo from '../../assets/media/flexbee-no-bg.webp';
import {
  PageWrapper,
  ThirdOrb,
  GridOverlay,
  FormCard,
  LogoContainer,
  FormTitle,
  Form,
  GradientButton,
  SuccessMessage,
  ErrorMessage,
} from './styles';

export default function SetPasswordPage({ history }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userId = params.get('userId');
  const code = params.get('code');

  const [password, setPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  // Kişinin şirketinin kuralı. Okuma başarısızsa varsayılan (8 karakter) gösterilir; sunucu
  // kayıtta gerçek kuralı yine uygular ve ihlali toast'lar.
  const [policy, setPolicy] = useState(DEFAULT_PASSWORD_POLICY);

  const isValidLink = userId && code;

  useEffect(() => {
    if (!userId) return;
    getPasswordPolicy(userId).then((res) => {
      if (res?.data?.data) setPolicy(res.data.data);
    });
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordAllowed(password, policy)) {
      setError(t('passwordPolicy.notMet'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('setPassword.mismatch'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await setPassword({ userId, code, password });
      setSuccess(true);
      await logout();
      setTimeout(() => history.push('/login'), 2000);
    } catch {
      // Error toast handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <ThirdOrb />
      <GridOverlay />
      <FormCard>
        <LogoContainer>
          <img src={flexbeeLogo} alt="Flexbee" />
        </LogoContainer>
        {!isValidLink ? (
          <ErrorMessage>
            <AlertCircle size={20} />
            {t('setPassword.invalidLink')}
          </ErrorMessage>
        ) : success ? (
          <SuccessMessage>
            <CheckCircle size={20} />
            {t('setPassword.success')}
          </SuccessMessage>
        ) : (
          <>
            <FormTitle>{t('setPassword.title')}</FormTitle>
            <Form onSubmit={handleSubmit}>
              <TextInput
                label={t('setPassword.newPassword')}
                type="password"
                value={password}
                onChange={(e) => { setPasswordValue(e.target.value); setError(''); }}
                required
                autoFocus
              />
              <TextInput
                label={t('setPassword.confirmPassword')}
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                required
                error={error}
              />
              <PasswordPolicyChecklist password={password} policy={policy} tone="dark" />
              <GradientButton type="submit" disabled={loading}>
                <Lock size={18} />
                {loading ? t('common.loading') : t('setPassword.submit')}
              </GradientButton>
            </Form>
          </>
        )}
      </FormCard>
    </PageWrapper>
  );
}
